/// <reference types="vite/client" />
import { useEffect, useMemo, useState } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout";
import type { Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./DashboardGrid.css";
import DashboardTile from "./DashboardTile";
import type { TileGrid, UserReport } from "../../types/reports";

const ResponsiveGrid = WidthProvider(GridLayout);

interface DashboardTileGridProps {
  tiles: UserReport[];
  refreshKey?: number;
  onLayoutStop: (items: Layout[]) => void;
  onUnpinned: () => void;
}

export default function DashboardTileGrid({ tiles, refreshKey, onLayoutStop, onUnpinned }: DashboardTileGridProps) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    function handler() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const persist = (layout: Layout[]) => onLayoutStop(layout);

  const gridById = useMemo(() => {
    const map: Record<string, TileGrid> = {};
    const placed = tiles
      .map((t) => t.dashboard_config?.grid)
      .filter((g): g is TileGrid => !!g);
    const baseY = placed.reduce((max, g) => Math.max(max, g.y + g.h), 0);

    tiles.forEach((t) => {
      if (t.dashboard_config?.grid) map[t.id] = t.dashboard_config.grid;
    });

    tiles
      .filter((t) => !t.dashboard_config?.grid)
      .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""))
      .forEach((t, i) => {
        map[t.id] = { x: (i % 3) * 4, y: baseY + Math.floor(i / 3) * 7, w: 4, h: 7 };
      });

    return map;
  }, [tiles]);

  if (isMobile) {
    return (
      <div className="flex flex-col gap-4">
        {tiles.map((report) => (
          <div key={report.id} className="min-h-[280px] w-full">
            <DashboardTile report={report} refreshKey={refreshKey} onUnpinned={onUnpinned} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <ResponsiveGrid
      cols={12}
      rowHeight={20}
      margin={[16, 16]}
      containerPadding={[0, 0]}
      draggableHandle=".drag-handle"
      draggableCancel="button"
      resizeHandles={["se"]}
      onDragStop={persist}
      onResizeStop={persist}
    >
      {tiles.map((report) => (
        <div
          key={report.id}
          className="h-full"
          data-grid={gridById[report.id]}
        >
          <DashboardTile report={report} refreshKey={refreshKey} onUnpinned={onUnpinned} />
        </div>
      ))}
    </ResponsiveGrid>
  );
}
